<template>
  <SpmOverlayPanel
    class-trigger="relation-popover-trigger"
    class-header="relation-popover__header"
    class-content="relation-popover__content"
    min-panel-width="320px"
    max-panel-width="380px"
    :close-on-route-change="false"
  >
    <template #trigger>
      <button
        type="button"
        class="relation-popover-trigger__button"
        :aria-label="t('customData.listing.relationPopover.ariaLabel', { value: relationValue })"
        @click="onTriggerClick"
      >
        <i class="fa-light fa-circle-info relation-popover-trigger__icon" />
      </button>
    </template>

    <template #header>
      <div class="relation-popover__title-wrap">
        <span class="relation-popover__title">{{ preview?.title || fallbackTitle }}</span>
        <span class="relation-popover__subtitle">{{ fallbackSubtitle }}</span>
      </div>
    </template>

    <div
      v-if="loading"
      class="relation-popover__state"
    >
      <i class="fa-light fa-spinner-third fa-spin" />
      <span>{{ t('customData.listing.relationPopover.loading') }}</span>
    </div>

    <div
      v-else-if="errorMessage"
      class="relation-popover__state relation-popover__state--error"
    >
      <i class="fa-light fa-triangle-exclamation" />
      <span>{{ errorMessage }}</span>
    </div>

    <div
      v-else-if="!hasRecord"
      class="relation-popover__state"
    >
      <i class="fa-light fa-inbox" />
      <span>{{ t('customData.listing.relationPopover.noData') }}</span>
    </div>

    <div
      v-else-if="preview && preview.fields.length === 0"
      class="relation-popover__state"
    >
      <i class="fa-light fa-circle-info" />
      <span>{{ t('customData.listing.relationPopover.noRelevantDetails') }}</span>
    </div>

    <div
      v-else-if="preview"
      class="relation-popover__fields"
    >
      <div
        v-for="field in displayFields"
        :key="field.uiKey"
        class="relation-popover__field"
        :class="{ 'relation-popover__field--meta': field.isMeta }"
      >
        <span class="relation-popover__field-label">{{ field.label }}</span>
        <span
          class="relation-popover__field-value"
          :class="getFieldValueClass(field)"
        >
          <template v-if="field.fieldType === 'external_link' && field.href">
            <a
              :href="field.href"
              target="_blank"
              rel="noopener noreferrer"
              class="relation-popover__link-pill"
            >
              <i class="fa-light fa-link-simple" />
              <span>{{ getLinkDisplayText(field.href) }}</span>
              <i
                class="fa-regular fa-arrow-up-right-from-square"
                :aria-label="t('customData.listing.relationPopover.openLink')"
              />
            </a>
          </template>
          <template v-else>
            {{ getFieldDisplayValue(field) }}
          </template>
        </span>
        <button
          v-if="isFieldExpandable(field)"
          type="button"
          class="relation-popover__toggle"
          @click="toggleFieldExpansion(field.uiKey)"
        >
          {{ isFieldExpanded(field.uiKey) ? t('common.seeLess') : t('common.seeMore') }}
        </button>
      </div>
    </div>
  </SpmOverlayPanel>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { SchemaItem } from '@/composables/custom-data/customData';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import {
  resolveRelationPreview,
  CustomObjectRelationDefinition,
  RelationPreviewResult,
} from '@/composables/custom-data/relationResolver';

export default defineComponent({
  name: 'RelationPopoverCard',
  components: {
    SpmOverlayPanel,
  },
  props: {
    relation: {
      type: Object as PropType<CustomObjectRelationDefinition>,
      required: true,
    },
    relationValue: {
      type: [String, Number] as PropType<string | number>,
      required: true,
    },
    customSchemas: {
      type: Array as PropType<SchemaItem[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { t } = useI18n();
    const loading = ref(false);
    const preview = ref<RelationPreviewResult | null>(null);
    const errorMessage = ref('');
    const hasRecord = ref(true);
    const expandedFields: Ref<Record<string, boolean>> = ref({});
    const INLINE_TRUNCATE_LIMIT = 120;
    const MULTILINE_TRUNCATE_LIMIT = 180;

    const requestKey = computed(() => [
      props.relation.targetSchemaType,
      props.relation.targetSchema,
      props.relation.targetField,
      String(props.relationValue),
    ].join(':'));

    const fallbackTitle = computed(() => props.relation.targetSchema);
    const fallbackSubtitle = computed(() => (
      props.relation.targetSchemaType === 'system'
        ? t('customData.listing.relationPopover.subtitle.system')
        : t('customData.listing.relationPopover.subtitle.custom')
    ));

    const isMetaField = (fieldKey: string): boolean => (
      fieldKey === '__meta_created_at' || fieldKey === '__meta_updated_at'
    );

    const getTranslatedFieldLabel = (fieldKey: string, fallbackLabel: string): string => {
      if (fieldKey === '__meta_created_at') {
        return t('customData.listing.default_fields_name.created_at');
      }
      if (fieldKey === '__meta_updated_at') {
        return t('customData.listing.default_fields_name.updated_at');
      }
      if (fieldKey === 'created_at' || fieldKey === 'date_creation') {
        return t('customData.listing.default_fields_name.created_at');
      }
      if (fieldKey === 'updated_at' || fieldKey === 'date_modification') {
        return t('customData.listing.default_fields_name.updated_at');
      }
      if (fieldKey === 'contact_first_name') {
        return t('myLists.manage.customers.headers.firstname');
      }
      if (fieldKey === 'contact_last_name') {
        return t('myLists.manage.customers.headers.lastname');
      }
      if (fieldKey === 'contact_data_email') {
        return t('myLists.manage.customers.headers.email');
      }
      if (fieldKey === 'reference') {
        return t('myShopData.products.headers.reference');
      }
      if (fieldKey === 'name') {
        return t('myShopData.products.headers.name');
      }
      if (fieldKey === 'link') {
        return t('myShopData.products.headers.link');
      }
      if (fieldKey === 'price') {
        return t('myShopData.products.headers.price');
      }
      return fallbackLabel;
    };

    const getLinkDisplayText = (href: string): string => {
      try {
        const parsedUrl = new URL(href);
        return parsedUrl.hostname.replace(/^www\./, '');
      } catch (error) {
        return t('customData.listing.relationPopover.openLink');
      }
    };

    const displayFields = computed(() => {
      if (!preview.value) return [];

      return preview.value.fields
        .map((field, index) => ({
          ...field,
          uiKey: `${field.key}-${index}`,
          label: getTranslatedFieldLabel(field.key, field.label),
          isMeta: isMetaField(field.key),
        }))
        .sort((fieldA, fieldB) => Number(fieldA.isMeta) - Number(fieldB.isMeta));
    });

    const isFieldExpanded = (fieldUiKey: string): boolean => !!expandedFields.value[fieldUiKey];

    const toggleFieldExpansion = (fieldUiKey: string) => {
      expandedFields.value = {
        ...expandedFields.value,
        [fieldUiKey]: !expandedFields.value[fieldUiKey],
      };
    };

    const isMultilineField = (field: any): boolean => field.displayMode === 'multiline';

    const isFieldExpandable = (field: any): boolean => {
      if (field.fieldType === 'external_link') return false;
      if (!field.value) return false;
      const limit = isMultilineField(field) ? MULTILINE_TRUNCATE_LIMIT : INLINE_TRUNCATE_LIMIT;
      return String(field.value).length > limit;
    };

    const getFieldDisplayValue = (field: any): string => {
      if (!isFieldExpandable(field)) return field.value;
      if (isFieldExpanded(field.uiKey)) return field.value;
      if (isMultilineField(field)) return field.value;
      return `${String(field.value).slice(0, INLINE_TRUNCATE_LIMIT)}...`;
    };

    const getFieldValueClass = (field: any): string => {
      if (field.fieldType === 'external_link') return '';
      if (!isFieldExpandable(field)) return '';
      if (isFieldExpanded(field.uiKey)) return 'relation-popover__field-value--expanded';
      if (isMultilineField(field)) return 'relation-popover__field-value--multiline-collapsed';
      return 'relation-popover__field-value--inline-collapsed';
    };

    const loadPreview = async (forceRefresh = false) => {
      loading.value = true;
      errorMessage.value = '';
      try {
        const response = await resolveRelationPreview({
          relation: props.relation,
          relationValue: props.relationValue,
          customSchemas: props.customSchemas,
          forceRefresh,
        });
        preview.value = response;
        hasRecord.value = !!response;
      } catch (error) {
        preview.value = null;
        hasRecord.value = false;
        errorMessage.value = t('customData.listing.relationPopover.error');
      } finally {
        loading.value = false;
      }
    };

    const onTriggerClick = async () => {
      await loadPreview(true);
    };

    watch(requestKey, () => {
      preview.value = null;
      errorMessage.value = '';
      hasRecord.value = true;
      expandedFields.value = {};
    });

    return {
      t,
      loading,
      preview,
      errorMessage,
      hasRecord,
      displayFields,
      isFieldExpanded,
      isFieldExpandable,
      getFieldDisplayValue,
      getFieldValueClass,
      toggleFieldExpansion,
      fallbackTitle,
      fallbackSubtitle,
      getLinkDisplayText,
      onTriggerClick,
    };
  },
});
</script>

<style lang="scss">
.relation-popover-trigger {
  display: inline-flex;
  align-items: center;
}

.relation-popover-trigger__button {
  width: 1.35rem;
  height: 1.35rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-color-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: var(--surface-200);
    color: var(--text-color);
  }
}

.relation-popover-trigger__icon {
  font-size: 0.85rem;
}

.relation-popover__header {
  padding: 0.75rem 1rem 0.6rem;
  background: linear-gradient(180deg, var(--surface-50) 0%, var(--surface-0) 100%);
}

.relation-popover__title-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.relation-popover__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-color);
}

.relation-popover__subtitle {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.relation-popover__content {
  padding: 0.72rem 1rem 0.85rem;
}

.relation-popover__state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  color: var(--text-color-secondary);
  padding: 0.55rem 0.2rem;
}

.relation-popover__state--error {
  color: var(--red-500);
}

.relation-popover__fields {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.relation-popover__field {
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
  padding: 0.5rem 0.75rem;
  margin: 0 0.15rem;
  border: 1px solid var(--surface-200);
  border-radius: 10px;
  background: var(--surface-50);
}

.relation-popover__field--meta {
  background: var(--surface-50);
}

.relation-popover__field-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-color-secondary);
}

.relation-popover__field-value {
  font-size: 0.86rem;
  color: var(--text-color);
  font-weight: 400;
  word-break: break-word;
}

.relation-popover__field-value--inline-collapsed {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.relation-popover__field-value--multiline-collapsed {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
}

.relation-popover__field-value--expanded {
  white-space: pre-wrap;
}

.relation-popover__toggle {
  margin-top: 0.2rem;
  align-self: flex-start;
  border: 0;
  background: transparent;
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
}

.relation-popover__link-pill {
  color: var(--text-color);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid var(--surface-300);
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  background: var(--surface-0);
  font-size: 0.8rem;
  max-width: 100%;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: var(--primary-color);
    border-color: var(--primary-300);
    background: var(--primary-50);
  }
}
</style>
